import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleMinusIcon,
  CircleXIcon,
} from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash';
import { upperFirst } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import styled from 'styled-components';

import { SsnMasker } from 'dashboard/components/SsnMasker';
import { FlagIcon } from 'dashboard/components-lib/FlagIcon/FlagIcon';
import TableNullState from 'dashboard/layouts/TableLayoutV2/TableNullState';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { GroupComparisonResult, MatchGroupResult } from 'dashboard/models/check';
import { ComparisonNameMap } from 'dashboard/models/check-result/check-requirement';
import Badge from 'design-system/components/Badge';
import { Title } from 'design-system/components/Card/Header';
import Select, { Option } from 'design-system/components/Select';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import HelperTooltip from 'design-system/components/Tooltip/HelperTooltip';
import { paragraphMixins } from 'design-system/mixins/text';

const HeadingContainer = styled('div')`
  display: flex;
`;

const Space = styled('div')`
  margin-left: ${Spacing[4]};
  display: flex;
`;

const StyledFlagIcon = styled(FlagIcon)`
  left: -${Spacing[4]};
`;

const MatchGroupInfoContainer = styled('div')`
  margin-left: auto;
  display: flex;
`;

const StyledSelect = styled(Select)`
  width: auto;
  left: 0;
  padding-left: 0px;
`;

const CircleErrorIcon = styled(CircleXIcon).attrs({ color: Color.BorderIconDanger })`
  vertical-align: middle;
`;

export const CirclePassedIcon = styled(CircleCheckIcon).attrs({ color: Color.BorderIconSuccess })`
  vertical-align: middle;
`;

interface ComparisonChecksTableProps {
  rows: ComparisonCheckRow[];
  matchResultHeaderTooltipContent?: ReactNode;
  showRecord?: boolean;
  isRedacted?: boolean;
  countryCode?: string;
  columns?: ComparisonCheckTableColumn[];
  handleSetMatchGroup?: (matchGroup: string) => void;
  matchGroupsResults?: MatchGroupResult[];
  selectedMatchGroup?: string;
}

type ComparisonCheckTableColumn = keyof ComparisonCheckRowData;

const COLUMN_HEADER_TITLE: { [K in ComparisonCheckTableColumn]?: string } = {
  status: undefined, // N/A, this column supports the status icon
  property: 'Property',
  submitted: 'Submitted',
  required: 'Required',
  matchResult: 'Match Result',
  record: 'Record',
  minimumMatch: 'Minimum Match',
};

export type ComparisonCheckPropertyType =
  | 'date'
  | 'social_security_number'
  | 'phone_number'
  | 'address';

export interface ComparisonResult {
  type: string;
  'match-level-minimum': string;
  'match-result': string;
  status: 'passed' | 'failed';
}

interface ComparisonCheckRowData {
  status: 'passed' | 'failed' | 'not_applicable';
  property: string;
  submitted: string | ReactNode;
  required?: boolean;
  record?: string;
  minimumMatch?: string;
  matchResult: string;
}

export interface ComparisonCheckRow {
  type?: ComparisonCheckPropertyType | undefined;
  data: ComparisonCheckRowData;
  comparisons?: ComparisonResult[] | GroupComparisonResult[];
}

const ComparisonChecksTable = ({
  rows,
  matchResultHeaderTooltipContent,
  columns = ['status', 'property', 'submitted', 'required', 'matchResult'],
  isRedacted = false,
  handleSetMatchGroup,
  matchGroupsResults,
  selectedMatchGroup,
  countryCode,
}: ComparisonChecksTableProps) => {
  const columnContents = columns.map((column) => {
    return (
      <TableHeaderCell key={column} width={column === 'status' ? 'min' : 'auto'}>
        {column === 'matchResult' ? (
          <TextWithHelperIcon>
            {COLUMN_HEADER_TITLE[column]}
            {matchResultHeaderTooltipContent && (
              <HelperTooltip size='sm' side='right'>
                {matchResultHeaderTooltipContent}
              </HelperTooltip>
            )}
          </TextWithHelperIcon>
        ) : (
          COLUMN_HEADER_TITLE[column]
        )}
      </TableHeaderCell>
    );
  });

  if (isEmpty(rows)) {
    return (
      <>
        <Table>
          <TableHead>
            <TableRow>{columnContents}</TableRow>
          </TableHead>
        </Table>
        <TableNullState dataTestBlock='database-checks-null-state'>
          No comparison checks were performed for this verification.
        </TableNullState>
      </>
    );
  }

  return (
    <>
      {matchGroupsResults && matchGroupsResults.length > 0 && (
        // Create the select dropdown for match groups when there are match results
        <HeadingContainer>
          <Title variant='section'>
            Match Group Result:{' '}
            {matchGroupsResults[Number(selectedMatchGroup)]['match-requirement-results'].every(
              (result) => result.status === 'passed',
            ) ? (
              <CirclePassedIcon />
            ) : (
              <CircleErrorIcon />
            )}
          </Title>
          <MatchGroupInfoContainer>
            <StyledSelect value={selectedMatchGroup} onValueChange={handleSetMatchGroup}>
              {matchGroupsResults.map((result, index) => {
                return (
                  <Option value={index.toString()} key={index}>
                    {countryCode ? (
                      <Space>
                        <StyledFlagIcon flagCode={countryCode} />
                        {upperFirst((result.description || result.name) as string)}
                      </Space>
                    ) : (
                      upperFirst((result.description || result.name) as string)
                    )}
                  </Option>
                );
              })}
            </StyledSelect>
          </MatchGroupInfoContainer>
        </HeadingContainer>
      )}
      <Table>
        <TableHead>
          <TableRow>{columnContents}</TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            return (
              <TableRow key={index}>
                {columns.map((column) => {
                  return (
                    <StyledTableCell key={`${index}-${column}`}>
                      {getTableBodyCell(row, column, isRedacted)}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};

const getTableBodyCell = (
  row: ComparisonCheckRow,
  column: ComparisonCheckTableColumn,
  isRedacted?: boolean,
): ReactNode | string => {
  switch (column) {
    case 'status':
      return getStatusIcon(row.data.status);
    case 'submitted': /* falls through */
    case 'record':
      return getFormattedProperty(row.data[column], row.type, isRedacted);
    case 'required':
      return row.data.required ? 'Yes' : 'No';
    case 'matchResult':
      return (
        <TextWithHelperIcon>
          {row.data.matchResult}
          {row.comparisons && (
            <HelperTooltip side='right' size='sm'>
              {getFormattedComparisonResults(row.comparisons)}
            </HelperTooltip>
          )}
        </TextWithHelperIcon>
      );
    default:
      return row.data[column];
  }
};

export const getStatusIcon = (status: 'passed' | 'failed' | 'not_applicable'): ReactNode | null => {
  switch (status) {
    case 'passed':
      return <CircleCheckIcon color={Color.CoreGreen700} />;
    case 'failed':
      return <CircleAlertIcon color={Color.CoreRed700} />;
    case 'not_applicable':
      return <CircleMinusIcon color={Color.CoreYellow700} />;
    default:
      return null;
  }
};

const getFormattedProperty = (
  value: string | ReactNode,
  type?: ComparisonCheckPropertyType,
  isRedacted?: boolean,
): ReactNode | string => {
  if (isRedacted) {
    return <Badge>Redacted</Badge>;
  }

  if (typeof value === 'string') {
    switch (type) {
      case 'date':
        return `${formatDate(value)} (${Math.floor(
          DateTime.local().diff(DateTime.fromISO(value), 'years').years,
        )} years old)`;
      case 'phone_number': {
        const formattedPhoneNumber = formatPhoneNumber(value, 'National');
        return isEmpty(formattedPhoneNumber) ? value : formattedPhoneNumber;
      }
      case 'social_security_number':
        return <SsnMasker ssn={value} />;
    }
  }

  return value;
};

const getFormattedComparisonResults = (
  results: ComparisonResult[] | GroupComparisonResult[],
): ReactNode => {
  // Handle a group comparison result

  return (
    <ComparisonResults>
      {results.map((result) => {
        // Handle either a ComparisonResult or a GroupComparisonResult
        const resultType = 'comparison-type' in result ? result['comparison-type'] : result.type;

        return (
          <li key={resultType}>
            {getStatusIcon(result.status)}
            <span>{ComparisonNameMap[resultType]}</span>
            <span>(min: {result['match-level-minimum']})</span>
          </li>
        );
      })}
    </ComparisonResults>
  );
};

const ComparisonResults = styled.ul`
  display: flex;
  flex-direction: column;
  grid-gap: ${Spacing[1]};

  & > li {
    display: flex;
    grid-gap: ${Spacing[1]};
    align-items: center;
    ${paragraphMixins['sm']}
  }
`;

const TextWithHelperIcon = styled.span`
  display: flex;
  grid-gap: ${Spacing[1]};
`;

const StyledTableCell = styled(TableCell)`
  white-space: wrap;
`;

export default ComparisonChecksTable;
